import React, { useState, useEffect, useCallback } from 'react';
import config from '../config';

const API_URL = config.API_URL;
const GOOGLE_CLIENT_ID = config.GOOGLE_CLIENT_ID;

// Custom hook for responsive design
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

const getStyles = (isMobile) => ({
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: isMobile ? 'flex-end' : 'center',
    justifyContent: 'center',
    zIndex: 200,
  },
  modalContent: {
    background: '#ffffff',
    padding: isMobile ? '2rem 1.5rem' : '2.5rem',
    borderRadius: isMobile ? '20px 20px 0 0' : '16px',
    width: isMobile ? '100%' : '90%',
    maxWidth: '420px',
    position: 'relative',
    maxHeight: isMobile ? '90vh' : 'auto',
    overflowY: 'auto',
    textAlign: 'center',
  },
  modalClose: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#666666',
    padding: '0.25rem',
  },
  logoContainer: {
    marginBottom: '1.5rem',
  },
  logoIcon: {
    width: '60px',
    height: '60px',
    background: 'linear-gradient(135deg, #c41e3a 0%, #a01830 100%)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 1rem',
    fontSize: '1.8rem',
  },
  modalTitle: {
    color: '#333',
    marginBottom: '0.5rem',
    fontSize: isMobile ? '1.5rem' : '1.75rem',
    fontWeight: 700,
  },
  modalSubtitle: {
    color: '#666',
    fontSize: '0.95rem',
    marginBottom: '2rem',
  },
  googleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    width: '100%',
    padding: '14px 24px',
    background: '#ffffff',
    border: '2px solid #e0e0e0',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: 600,
    color: '#333',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginBottom: '1.5rem',
  },
  googleIcon: {
    width: '20px',
    height: '20px',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '1.5rem 0',
    color: '#999',
    fontSize: '0.85rem',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    background: '#e0e0e0',
  },
  dividerText: {
    padding: '0 1rem',
  },
  benefits: {
    textAlign: 'left',
    background: '#fef2f2',
    borderRadius: '12px',
    padding: '1.25rem',
    marginTop: '1rem',
  },
  benefitItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '0.75rem',
    fontSize: '0.9rem',
    color: '#555',
  },
  benefitIcon: {
    color: '#c41e3a',
    fontSize: '1.1rem',
  },
  errorMessage: {
    background: '#fee2e2',
    color: '#dc2626',
    padding: '0.75rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255, 255, 255, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '16px',
    zIndex: 10,
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #c41e3a',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
});

function Login({ onClose, onLogin }) {
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const { width } = useWindowSize();
  const isMobile = width <= 768;
  const styles = getStyles(isMobile);

  const handleGoogleResponse = useCallback(async (response) => {
    if (response.credential) {
      setLoading(true);
      setApiError('');

      try {
        const res = await fetch(`${API_URL}/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credential: response.credential })
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || 'Authentication failed');
        }

        // Store token and user data
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));

        // Call onLogin with user data
        onLogin({
          name: data.user.name,
          email: data.user.email,
          phone: data.user.phone || '',
          id: data.user.id,
          picture: data.user.picture || ''
        });
      } catch (error) {
        console.error('Login error:', error);
        setApiError(error.message || 'Failed to sign in. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  }, [onLogin]);

  useEffect(() => {
    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setGoogleLoaded(true);
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript) {
        // Don't remove script as it might be used elsewhere
      }
    };
  }, []);

  useEffect(() => {
    if (googleLoaded && window.google) {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
      });

      window.google.accounts.id.renderButton(
        document.getElementById('google-signin-button'),
        {
          theme: 'outline',
          size: 'large',
          width: '100%',
          text: 'continue_with',
          shape: 'rectangular',
        }
      );
    }
  }, [googleLoaded, handleGoogleResponse]);

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button style={styles.modalClose} onClick={onClose}>✕</button>
        
        {loading && (
          <div style={styles.loadingOverlay}>
            <div style={styles.spinner}></div>
          </div>
        )}

        <div style={styles.logoContainer}>
          <div style={styles.logoIcon}>🏥</div>
        </div>

        <h2 style={styles.modalTitle}>Welcome to DocClinic</h2>
        <p style={styles.modalSubtitle}>Sign in to book appointments and manage your health</p>

        {apiError && (
          <div style={styles.errorMessage}>
            {apiError}
          </div>
        )}

        {/* Google Sign-In Button Container */}
        <div id="google-signin-button" style={{ display: 'flex', justifyContent: 'center' }}></div>

        {!googleLoaded && (
          <button style={styles.googleButton} disabled>
            <svg style={styles.googleIcon} viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Loading...
          </button>
        )}

        <div style={styles.benefits}>
          <div style={styles.benefitItem}>
            <span style={styles.benefitIcon}>✓</span>
            <span>Quick & secure sign-in with Google</span>
          </div>
          <div style={styles.benefitItem}>
            <span style={styles.benefitIcon}>✓</span>
            <span>No passwords to remember</span>
          </div>
          <div style={styles.benefitItem}>
            <span style={styles.benefitIcon}>✓</span>
            <span>Book and manage appointments easily</span>
          </div>
          <div style={{ ...styles.benefitItem, marginBottom: 0 }}>
            <span style={styles.benefitIcon}>✓</span>
            <span>Your data is safe and private</span>
          </div>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}

export default Login;
